import React, { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Link as LinkIcon, Undo2, Redo2, AlignLeft, AlignCenter, AlignRight,
} from "lucide-react";

function ToolbarBtn({ active, onClick, title, disabled, children, testId }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      className={`p-2 rounded-sm border text-sm transition-colors ${
        active ? "bg-navy text-cream border-navy" : "bg-white text-navy border-navy/15 hover:border-gold"
      } disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

export default function RichEditor({ value, onChange, dir = "ltr", placeholder = "", testId }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "underline decoration-gold" } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange && onChange(ed.getHTML()),
    editorProps: {
      attributes: {
        dir,
        class: "prose max-w-none min-h-[220px] p-4 focus:outline-none text-navy leading-relaxed",
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (value && value !== editor.getHTML()) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div data-testid={testId} className="border border-navy/15 rounded-md bg-cream">
      <div className="flex flex-wrap gap-1 p-2 border-b border-navy/10 bg-white">
        <ToolbarBtn testId={`${testId}-bold`} title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={14} /></ToolbarBtn>
        <ToolbarBtn testId={`${testId}-italic`} title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={14} /></ToolbarBtn>
        <ToolbarBtn testId={`${testId}-underline`} title="Underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon size={14} /></ToolbarBtn>
        <span className="w-px h-6 bg-navy/10 mx-1 self-center" />
        <ToolbarBtn testId={`${testId}-h1`} title="H1" active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 size={14} /></ToolbarBtn>
        <ToolbarBtn testId={`${testId}-h2`} title="H2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={14} /></ToolbarBtn>
        <ToolbarBtn testId={`${testId}-h3`} title="H3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={14} /></ToolbarBtn>
        <span className="w-px h-6 bg-navy/10 mx-1 self-center" />
        <ToolbarBtn testId={`${testId}-bullet`} title="Bulleted list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={14} /></ToolbarBtn>
        <ToolbarBtn testId={`${testId}-ordered`} title="Ordered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={14} /></ToolbarBtn>
        <ToolbarBtn testId={`${testId}-quote`} title="Blockquote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={14} /></ToolbarBtn>
        <span className="w-px h-6 bg-navy/10 mx-1 self-center" />
        <ToolbarBtn testId={`${testId}-align-l`} title="Align start" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft size={14} /></ToolbarBtn>
        <ToolbarBtn testId={`${testId}-align-c`} title="Align center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter size={14} /></ToolbarBtn>
        <ToolbarBtn testId={`${testId}-align-r`} title="Align end" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight size={14} /></ToolbarBtn>
        <span className="w-px h-6 bg-navy/10 mx-1 self-center" />
        <ToolbarBtn testId={`${testId}-link`} title="Insert link" active={editor.isActive("link")} onClick={setLink}><LinkIcon size={14} /></ToolbarBtn>
        <span className="ms-auto flex gap-1">
          <ToolbarBtn testId={`${testId}-undo`} title="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo2 size={14} /></ToolbarBtn>
          <ToolbarBtn testId={`${testId}-redo`} title="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo2 size={14} /></ToolbarBtn>
        </span>
      </div>
      <div style={placeholder && !value ? { position: "relative" } : {}}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
